'use client';

import { type CSSProperties } from 'react';
import Link from 'next/link';

import { useQuery } from '@tanstack/react-query';
import { CalendarClock, UserCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';
import { formatLocaleDate } from '@kit/shared/utils';

import { Capabilities } from '@kit/capabilities';
import { useCapabilityOptional } from '@kit/capabilities/client';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  HorizontalCardScroller,
  SectionHeader,
} from '~/(tenant)/_components/native/_primitives';

interface RecentGuestPassesSectionProps {
  accountId: string;
}

type GuestPassStatus =
  | 'pending_payment'
  | 'active'
  | 'used'
  | 'expired'
  | 'revoked'
  | 'refunded';

interface RecentPassRow {
  id: string;
  guest_name: string;
  guest_email: string | null;
  guest_phone: string | null;
  valid_for_date: string;
  visit_time: string | null;
  status: GuestPassStatus;
}

type SupabaseLike = {
  from: (table: string) => UntypedQuery;
};
type UntypedQuery = {
  select: (columns: string) => UntypedQuery;
  eq: (col: string, val: unknown) => UntypedQuery;
  order: (col: string, opts: { ascending: boolean }) => UntypedQuery;
  limit: (n: number) => Promise<{ data: RecentPassRow[] | null }>;
};

const MAX_ROWS = 8;

/**
 * Backward-looking companion to the existing `guest-allowance` section.
 * Where allowance answers "what can I do today?", this answers "who have I
 * invited recently?". Mirrors the booking-category visual rhythm (220-wide
 * cards in a horizontal scroller).
 *
 * Hidden when:
 *   - tenant has no GUEST_PASSES capability
 *   - the user is signed out
 *   - the user has invited zero guests at this tenant
 *
 * Rows are sorted today-first, then upcoming ascending, then past
 * descending — same comparator used by /guests page.
 */
export function RecentGuestPassesSection({
  accountId,
}: RecentGuestPassesSectionProps) {
  const enabled = useCapabilityOptional(Capabilities.GUEST_PASSES);
  const client = useSupabase();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
    enabled,
  });

  const userId = session?.user?.id;

  const { data: passes } = useQuery({
    queryKey: ['native-home-recent-guests', accountId, userId],
    enabled: enabled && Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<RecentPassRow[]> => {
      if (!userId) return [];

      // Cast: guest_passes typed shape lands in the next typegen run
      // (mirrors what the existing guest-allowance section does).
      const c = client as unknown as SupabaseLike;

      // Order by visit date descending (closest-recent first) so the today/
      // upcoming buckets sit at the head of the row set before the in-memory
      // comparator below re-orders them today-first. Matches the row set the
      // /guests page loader sees — diverging here would mean a member with
      // many old passes could see different "recent" cards on home vs /guests.
      const { data } = await c
        .from('guest_passes')
        .select(
          'id, guest_name, guest_email, guest_phone, valid_for_date, visit_time, status',
        )
        .eq('account_id', accountId)
        .eq('invited_by_user_id', userId)
        .eq('is_deleted', false)
        .order('valid_for_date', { ascending: false })
        .limit(24);

      const today = new Date().toISOString().slice(0, 10);
      const bucket = (d: string) => (d === today ? 0 : d > today ? 1 : 2);

      const rows = (data ?? []).slice().sort((a, b) => {
        const ba = bucket(a.valid_for_date);
        const bb = bucket(b.valid_for_date);
        if (ba !== bb) return ba - bb;
        if (ba === 1) return a.valid_for_date.localeCompare(b.valid_for_date);
        if (ba === 2) return b.valid_for_date.localeCompare(a.valid_for_date);
        return 0;
      });

      return rows.slice(0, MAX_ROWS);
    },
  });

  if (!enabled) return null;
  if (!userId) return null;
  if (!passes || passes.length === 0) return null;

  return (
    <section style={{ paddingTop: 32 }} data-test="native-recent-guest-passes">
      <SectionHeader
        eyebrow={
          <Trans
            i18nKey="common:nativeHome.recentGuests.eyebrow"
            defaults="Guests"
          />
        }
        title={
          <Trans
            i18nKey="common:nativeHome.recentGuests.title"
            defaults="Recent invites"
          />
        }
        action={{
          label: (
            <Trans
              i18nKey="common:nativeHome.recentGuests.seeAll"
              defaults="All"
            />
          ),
          href: '/guests',
        }}
      />

      <HorizontalCardScroller>
        {passes.map((pass) => (
          <GuestPassCard key={pass.id} pass={pass} />
        ))}
      </HorizontalCardScroller>
    </section>
  );
}

function GuestPassCard({ pass }: { pass: RecentPassRow }) {
  const t = useNativeTokens();
  const { i18n } = useTranslation();

  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const tomorrowIso = new Date(today.getTime() + 86400000)
    .toISOString()
    .slice(0, 10);

  const dateLabel = (() => {
    if (pass.valid_for_date === todayIso) {
      return (
        <Trans
          i18nKey="common:nativeHome.recentGuests.todayLabel"
          defaults="Today"
        />
      );
    }
    if (pass.valid_for_date === tomorrowIso) {
      return (
        <Trans
          i18nKey="common:nativeHome.recentGuests.tomorrowLabel"
          defaults="Tomorrow"
        />
      );
    }
    return formatLocaleDate(pass.valid_for_date, i18n.language, {
      day: 'numeric',
      month: 'short',
      year: undefined,
    });
  })();

  const contact = pass.guest_email ?? pass.guest_phone ?? '';

  return (
    <Link
      href={`/guests/${pass.id}`}
      style={{
        flexShrink: 0,
        width: 220,
        scrollSnapAlign: 'start',
        textDecoration: 'none',
        color: t.foreground,
      }}
    >
      <article
        style={{
          height: 168,
          padding: 18,
          borderRadius: t.radius,
          border: `1px solid ${t.line}`,
          backgroundColor: t.surface,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: t.fontMono,
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: t.muted,
          }}
        >
          <CalendarClock size={11} strokeWidth={1.6} />
          {dateLabel}
          {pass.visit_time ? ` · ${pass.visit_time.slice(0, 5)}` : ''}
        </div>

        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: t.fontDisplay,
              fontSize: 20,
              fontWeight: 400,
              letterSpacing: '-0.01em',
              lineHeight: 1.15,
              color: t.foreground,
            }}
          >
            <UserCircle2 size={18} strokeWidth={1.4} color={t.muted} />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {pass.guest_name}
            </span>
          </div>
          {contact ? (
            <div
              style={{
                marginTop: 4,
                fontFamily: t.fontSans,
                fontSize: 12,
                color: t.muted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {contact}
            </div>
          ) : null}
        </div>

        <StatusBadge status={pass.status} />
      </article>
    </Link>
  );
}

type PillTone = 'green' | 'amber' | 'red' | 'neutral';

const PILL_TONE_STYLES: Record<
  PillTone,
  { bg: string; fg: string; dot: string }
> = {
  green: { bg: '#E5F1E8', fg: '#2F6B3A', dot: '#3F8F4F' },
  amber: { bg: '#F8EFD7', fg: '#7A5616', dot: '#B68628' },
  red: { bg: '#F4E1DD', fg: '#86372C', dot: '#B25040' },
  neutral: { bg: 'rgba(0,0,0,0.05)', fg: 'inherit', dot: 'currentColor' },
};

function StatusBadge({ status }: { status: GuestPassStatus }) {
  const t = useNativeTokens();

  const { tone, labelKey, labelDefault } = (() => {
    switch (status) {
      case 'active':
        return {
          tone: 'green' as PillTone,
          labelKey: 'common:nativeHome.recentGuests.status.active',
          labelDefault: 'active',
        };
      case 'used':
        return {
          tone: 'green' as PillTone,
          labelKey: 'common:nativeHome.recentGuests.status.used',
          labelDefault: 'used',
        };
      case 'pending_payment':
        return {
          tone: 'amber' as PillTone,
          labelKey: 'common:nativeHome.recentGuests.status.pendingPayment',
          labelDefault: 'awaiting payment',
        };
      case 'expired':
        return {
          tone: 'red' as PillTone,
          labelKey: 'common:nativeHome.recentGuests.status.expired',
          labelDefault: 'expired',
        };
      case 'revoked':
        return {
          tone: 'red' as PillTone,
          labelKey: 'common:nativeHome.recentGuests.status.revoked',
          labelDefault: 'revoked',
        };
      case 'refunded':
        return {
          tone: 'neutral' as PillTone,
          labelKey: 'common:nativeHome.recentGuests.status.refunded',
          labelDefault: 'refunded',
        };
    }
  })();

  const palette = PILL_TONE_STYLES[tone];
  const fg = palette.fg === 'inherit' ? t.foreground : palette.fg;

  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    backgroundColor: palette.bg,
    color: fg,
    fontFamily: t.fontMono,
    fontSize: 9,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    fontWeight: 500,
    alignSelf: 'flex-start',
  };

  return (
    <span style={style} data-test="native-recent-guest-status">
      <span
        aria-hidden
        style={{
          width: 5,
          height: 5,
          borderRadius: 999,
          backgroundColor: palette.dot,
        }}
      />
      <Trans i18nKey={labelKey} defaults={labelDefault} />
    </span>
  );
}
